import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, ShoppingBag, Activity, BarChart3 } from 'lucide-react';
const projects = [{
  title: 'E-Commerce Platform',
  category: 'Web Development',
  description: 'A full-featured online store with real-time inventory and secure payments.',
  icon: ShoppingBag,
  gradient: 'from-blue-500 to-cyan-500',
  pattern: 'shopping'
}, {
  title: 'Health & Fitness App',
  category: 'Mobile App',
  description: 'Cross-platform mobile application for tracking workouts and nutrition.',
  icon: Activity,
  gradient: 'from-green-500 to-emerald-500',
  pattern: 'health'
}, {
  title: 'Corporate Dashboard',
  category: 'Web Application',
  description: 'Internal management tool for data visualization and employee tracking.',
  icon: BarChart3,
  gradient: 'from-purple-500 to-pink-500',
  pattern: 'dashboard'
}];
export function Portfolio() {
  return <section id="portfolio" className="py-20 bg-slate-950" data-id="element-225">
      <div className="container mx-auto px-4" data-id="element-226">
        <div className="text-center mb-16" data-id="element-227">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-228">
            Featured Projects
          </h2>
          <div className="w-20 h-1 bg-red-600 mx-auto mb-6" data-id="element-229"></div>
          <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-230">
            Explore some of our recent work and see how we've helped businesses
            achieve their digital goals.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-231">
          {projects.map((project, index) => <motion.div key={index} initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="group relative overflow-hidden rounded-xl bg-slate-900 border border-slate-800 hover:border-red-500/50 transition-all" data-id="element-232">
              {/* Case Study Image/Visual */}
              <div className={`h-48 w-full bg-gradient-to-br ${project.gradient} relative overflow-hidden`} data-id="element-233">
                {/* Decorative pattern overlay */}
                <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: project.pattern === 'shopping' ? 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.1) 10px, rgba(255,255,255,.1) 20px)' : project.pattern === 'health' ? 'radial-gradient(circle at 20% 50%, rgba(255,255,255,.2) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,.2) 0%, transparent 50%)' : 'linear-gradient(to right, rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,.1) 1px, transparent 1px)',
              backgroundSize: project.pattern === 'dashboard' ? '20px 20px' : 'auto'
            }} data-id="element-234"></div>

                {/* Icon overlay */}
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-235">
                  <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20 group-hover:scale-110 transition-transform" data-id="element-236">
                    <project.icon className="w-10 h-10 text-white" data-id="element-237" />
                  </div>
                </div>

                {/* Animated elements */}
                {project.pattern === 'shopping' && <>
                    <motion.div animate={{
                y: [0, -10, 0],
                x: [0, 5, 0]
              }} transition={{
                repeat: Infinity,
                duration: 3,
                ease: 'easeInOut'
              }} className="absolute top-4 right-4 w-12 h-12 bg-white/20 rounded-lg backdrop-blur-sm" data-id="element-238" />
                    <motion.div animate={{
                y: [0, 10, 0],
                x: [0, -5, 0]
              }} transition={{
                repeat: Infinity,
                duration: 4,
                ease: 'easeInOut',
                delay: 0.5
              }} className="absolute bottom-4 left-4 w-16 h-16 bg-white/20 rounded-full backdrop-blur-sm" data-id="element-239" />
                  </>}

                {project.pattern === 'health' && <>
                    <motion.div animate={{
                scale: [1, 1.2, 1]
              }} transition={{
                repeat: Infinity,
                duration: 2,
                ease: 'easeInOut'
              }} className="absolute top-1/4 left-1/4 w-8 h-8 bg-white/30 rounded-full backdrop-blur-sm" data-id="element-240" />
                    <motion.div animate={{
                scale: [1, 1.3, 1]
              }} transition={{
                repeat: Infinity,
                duration: 2.5,
                ease: 'easeInOut',
                delay: 0.3
              }} className="absolute bottom-1/4 right-1/4 w-6 h-6 bg-white/30 rounded-full backdrop-blur-sm" data-id="element-241" />
                  </>}

                {project.pattern === 'dashboard' && <div className="absolute inset-0 flex items-center justify-center opacity-30" data-id="element-242">
                    <div className="grid grid-cols-3 gap-2 p-4" data-id="element-243">
                      {[...Array(6)].map((_, i) => <motion.div key={i} animate={{
                  opacity: [0.3, 0.8, 0.3]
                }} transition={{
                  repeat: Infinity,
                  duration: 2,
                  delay: i * 0.2
                }} className="w-8 h-8 bg-white rounded" data-id="element-244" />)}
                    </div>
                  </div>}
              </div>

              <div className="p-6 relative z-10" data-id="element-245">
                <div className="text-xs font-bold text-red-500 uppercase tracking-wider mb-2" data-id="element-246">
                  {project.category}
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-red-400 transition-colors" data-id="element-247">
                  {project.title}
                </h3>
                <p className="text-slate-400 mb-4" data-id="element-248">{project.description}</p>
                <a href="#" className="inline-flex items-center gap-2 text-sm font-medium text-white hover:text-red-500 transition-colors" data-id="element-249">
                  View Case Study <ExternalLink className="w-4 h-4" data-id="element-250" />
                </a>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}